import { resolveConfidenceVisitorId } from "@helpers/confidenceVisitorId"
import { isNetworkError } from "@helpers/sentryFilter"
import { supabase } from "@helpers/supabase"
import { rememberTrackerUserId } from "@helpers/trackerUserId"
import * as Sentry from "@sentry/react"

// Guards against re-running for an account the listener has already handled.
// Several events carry a session and each is treated as an opportunity, so
// without this the merge would fire repeatedly for the same account.
let mergedForUserId: string | undefined

/**
 * Records the current Confidence visitor id against the signed-in account.
 *
 * The visitor id changes across browsers, devices and cookie resets, so every
 * trusted id is kept: the first one becomes `original_confidence_visitor_id`
 * and all of them accumulate in `confidence_visitor_ids`. The merge runs in
 * Postgres under a row lock, which is what makes concurrent sign-ins safe.
 *
 * Requires a session — the database function resolves the account from
 * `auth.uid()`.
 *
 * Returns whether the id was recorded, so the caller knows if a later attempt
 * is still needed.
 */
async function mergeWebConfidenceVisitorIdentity(): Promise<boolean> {
  // Read synchronously. This replaces a fifteen-second poll for the SDK's
  // cookie, which existed because the id used to arrive over the network while
  // auth events fire from local storage within milliseconds of a page load, so
  // the merge otherwise always ran first and found nothing.
  //
  // resolveConfidenceVisitorId mints an id rather than waiting for one, so
  // there is nothing left to wait for. Dropping the wait also removes the need
  // to track which account an attempt was started for: the RPC is now issued in
  // the same tick as the auth event that triggered it, so it cannot outlive its
  // own session and reach the server with no session or the wrong one.
  const confidenceVisitorId = resolveConfidenceVisitorId()

  if (!confidenceVisitorId) return false

  const { error } = await supabase.rpc(
    "merge_web_confidence_visitor_identity",
    {
      p_visitor_id: confidenceVisitorId,
    },
  )

  // Bookkeeping must never fail a sign-in, and there is deliberately no
  // client-side retry or read-modify-write fallback: a timed-out call may
  // already have committed, so writing back a stale array would drop ids.
  if (error) {
    // NOTE: A request that never got an answer is no error to act on.
    if (!isNetworkError(error)) Sentry.captureException(error)
    return false
  }

  return true
}

/**
 * Merges the visitor id whenever a session is present.
 *
 * A listener rather than a call inside each sign-in function, because
 * `signInWithOAuth` redirects the whole browser to the provider: the page that
 * called it is gone by the time a session exists, so an inline call there can
 * never run. The session lands on the page the provider redirects back to, and
 * this fires there.
 *
 * Every session-bearing event counts, not just SIGNED_IN. auth-js only emits
 * SIGNED_IN when it recovers a session that is still valid; once the access
 * token has expired it refreshes instead and emits TOKEN_REFRESHED, which is
 * what any user returning after an hour hits. Filtering on SIGNED_IN therefore
 * misses the most common return path, and the guard keeps the extra events
 * from turning into repeat work.
 *
 * Also re-attaches the account to the Snowplow tracker. The tracker holds its
 * user id in memory and every full page load builds a new one, while
 * `setUserId` is only called from the sign-in paths — so any load that is not
 * itself a sign-in reports as anonymous. Returning from Stripe is exactly that
 * load, and it is where the purchase happens: measured on Lingo web, only 2.4%
 * of payment-completion events carry a user id. Setting it from the auth state
 * instead covers every page load that has a session, however it started.
 *
 * Recorded through `rememberTrackerUserId` rather than handed straight to the
 * tracker, because this listener can run before the tracker exists — see that
 * module for why a bare `setUserId` would be dropped there.
 */
export function startWebConfidenceVisitorIdentitySync() {
  supabase.auth.onAuthStateChange((event, session) => {
    const userId = session?.user.id

    if (!userId) {
      if (event === "SIGNED_OUT") {
        mergedForUserId = undefined
        rememberTrackerUserId(undefined)
      }
      return
    }

    // Ahead of the merge guard on purpose. The merge is once per account, but
    // the tracker forgets on every page load and has to be told again each
    // time, so this cannot sit behind a guard that suppresses repeats.
    rememberTrackerUserId(userId)

    if (mergedForUserId === userId) return

    // Claimed up front so overlapping events do not each fire the merge, then
    // released again if nothing was recorded so the next event can retry.
    mergedForUserId = userId
    void mergeWebConfidenceVisitorIdentity().then((merged) => {
      if (!merged && mergedForUserId === userId) mergedForUserId = undefined
    })
  })
}
